/**
 * Iter 226 — Composant Manager des Templates de réponses rapides.
 * Inclus dans SettingsPage > API > Communications.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Plus, Trash2, Edit3, Save, X, Hash, MessageCircle, Video } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const VAR_HINTS = [
  "{{prospect.name}}",
  "{{prospect.first_name}}",
  "{{prospect.email}}",
  "{{prospect.phone}}",
  "{{thread.channel}}",
  "{{thread.sender_name}}",
  "{{user.first_name}}",
];

export default function MetaTemplatesManager() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | template_id
  const [form, setForm] = useState({ title: "", body: "", shortcut: "", tags: "", loom_url: "" });
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/meta/templates");
      setTemplates(r.data?.templates || []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  const startNew = () => {
    setEditing("new");
    setForm({ title: "", body: "", shortcut: "", tags: "", loom_url: "" });
  };

  const startEdit = (t) => {
    setEditing(t.template_id);
    setForm({
      title: t.title || "",
      body: t.body || "",
      shortcut: t.shortcut || "",
      tags: (t.tags || []).join(", "),
      loom_url: t.loom_url || "",
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm({ title: "", body: "", shortcut: "", tags: "", loom_url: "" });
  };

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      alert("Titre et corps sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body,
        shortcut: form.shortcut.trim() || undefined,
        loom_url: form.loom_url.trim() || undefined,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editing && editing !== "new") payload.template_id = editing;
      await api.post("/meta/templates", payload);
      cancel();
      await reload();
    } catch (e) {
      alert("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce template ?")) return;
    try {
      await api.delete(`/meta/templates/${id}`);
      await reload();
    } catch (e) {
      alert("Erreur : " + (e?.response?.data?.detail || e.message));
    }
  };

  const insertVar = (v) => {
    setForm((f) => ({ ...f, body: f.body + v }));
  };

  const inputCls = "bg-[#0d0a14] border-white/10 text-white placeholder:text-slate-600";

  return (
    <div className="bg-[#14101c] border border-white/10 rounded-xl p-6 space-y-4" data-testid="meta-templates-manager">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-violet-500/15">
            <MessageCircle className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Templates de réponses rapides Meta</h3>
            <p className="text-[11px] text-slate-500">Tapez <code className="text-amber-300">/cmd</code> dans l'inbox pour insérer instantanément. Variables {`{{prospect.name}}`} interpolées en temps réel.</p>
          </div>
        </div>
        {!editing && (
          <Button onClick={startNew} size="sm" className="bg-violet-500 hover:bg-violet-600 text-white" data-testid="tpl-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouveau
          </Button>
        )}
      </div>

      {/* Editor */}
      {editing && (
        <div className="bg-[#0b0811] border border-violet-500/30 rounded-lg p-4 space-y-3">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-slate-500 mb-1 block">Titre *</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Bonjour FAQ tarifs" className={inputCls} data-testid="tpl-title" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 mb-1 flex items-center gap-1"><Hash className="w-3 h-3" /> Raccourci (optionnel)</label>
              <Input value={form.shortcut} onChange={(e) => setForm({ ...form, shortcut: e.target.value.toLowerCase() })} placeholder="/bonjour" className={inputCls} data-testid="tpl-shortcut" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-slate-500 mb-1 block">Message</label>
            <Textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Bonjour {{prospect.name}} 👋, merci pour votre message…"
              rows={5}
              className={`${inputCls} font-mono`}
              data-testid="tpl-body"
            />
            <div className="flex flex-wrap gap-1 mt-2">
              <span className="text-[10px] text-slate-500 mr-1">Insérer :</span>
              {VAR_HINTS.map((v) => (
                <button key={v} onClick={() => insertVar(v)} type="button" className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-300 hover:bg-violet-500/20 font-mono" data-testid={`tpl-var-${v}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[11px] text-slate-500 mb-1 block">Tags (séparés par virgule)</label>
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="welcome, faq" className={inputCls} data-testid="tpl-tags" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500 mb-1 flex items-center gap-1">
              <Video className="w-3 h-3 text-rose-400" /> Lien vidéo Loom (optionnel)
            </label>
            <Input
              value={form.loom_url}
              onChange={(e) => setForm({ ...form, loom_url: e.target.value })}
              placeholder="https://www.loom.com/share/abc123..."
              className={inputCls}
              data-testid="tpl-loom-url"
            />
            <p className="text-[10px] text-slate-500 mt-1">
              Si renseigné, l'URL sera ajoutée à la fin du message envoyé (avec un préfixe 📹 Vidéo :).
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button onClick={cancel} variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid="tpl-cancel">
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={save} disabled={saving} className="bg-violet-500 hover:bg-violet-600 text-white" data-testid="tpl-save">
              <Save className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <p className="text-xs text-slate-500">Chargement…</p>
      ) : templates.length === 0 && !editing ? (
        <p className="text-xs text-slate-500 text-center py-4">Aucun template. Créez votre premier raccourci pour gagner du temps !</p>
      ) : (
        <ul className="space-y-2">
          {templates.map((t) => (
            <li key={t.template_id} className="bg-[#0b0811] border border-white/5 rounded-lg p-3" data-testid={`tpl-row-${t.template_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-bold text-white truncate">{t.title}</span>
                    {t.shortcut && <code className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">{t.shortcut}</code>}
                    {t.loom_url && (
                      <a href={t.loom_url} target="_blank" rel="noreferrer" className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 hover:bg-rose-500/25" title={t.loom_url}>
                        <Video className="w-2.5 h-2.5" /> Loom
                      </a>
                    )}
                    <span className="text-[9px] text-slate-500 ml-auto">utilisé {t.usage_count || 0}x</span>
                  </div>
                  <p className="text-[11px] text-slate-400 line-clamp-2 whitespace-pre-wrap font-mono">{t.body}</p>
                  {t.tags && t.tags.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {t.tags.map((tag) => (
                        <span key={tag} className="text-[9px] px-1 rounded bg-violet-500/10 text-violet-300">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(t)} className="text-slate-400 hover:text-white p-1" data-testid={`tpl-edit-${t.template_id}`}>
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(t.template_id)} className="text-slate-400 hover:text-rose-400 p-1" data-testid={`tpl-del-${t.template_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
